import {useState, useEffect, useCallback, useRef} from '@wordpress/element';
import {getSetting} from '@woocommerce/settings';
import {extractAddress, mapLineItems, mapShippingOptions} from '../helpers';
import {isFieldRequired} from "../../../utils";

export const useGooglePayButton = (
    {
        paypal,
        onClick,
        onClose,
        onError,
        settings,
        paymentData,
        needsShipping,
        paymentsClient,
        setPaymentData,
        buttonContainer,
    }) => {
    const [button, setButton] = useState(null);
    const currentData = useRef({settings});
    const data = getSetting('ppcp_googlepay_data');

    useEffect(() => {
        currentData.current = {
            ...currentData.current,
            settings,
            paymentData
        }
    }, [settings, paymentData]);

    /**
     * Reset button when payment data is cleared
     */
    useEffect(() => {
        if (!paymentData || Object.keys(paymentData).length === 0) {
            setButton(null);
        }
    }, [paymentData]);

    /**
     * Handle button click - initiates Google Pay payment flow
     */
    const onButtonClick = useCallback(async () => {
        try {
            if (onClick) {
                onClick();
            }

            // Build payment data request
            const paymentDataRequest = buildPaymentDataRequest({
                settings,
                needsShipping
            });

            // Load payment data from Google Pay
            const response = await paymentsClient.loadPaymentData(paymentDataRequest);

            const {
                shippingAddress,
                paymentMethodData
            } = response;

            const {paymentData} = currentData.current;

            if (paymentData.confirmation.status === 'PAYER_ACTION_REQUIRED') {
                await paypal.Googlepay().initiatePayerAction({orderId: paymentData.orderId});
            }

            // Payment data will be processed via onPaymentAuthorized callback
            // Just store it for now
            if (settings.isExpress) {
                setPaymentData({
                    email: paymentData.email,
                    billingAddress: extractAddress(paymentMethodData?.info?.billingAddress || {}),
                    ...(needsShipping && {
                        shippingAddress: extractAddress(shippingAddress)
                    })
                }, true);
            } else {
                setPaymentData({
                    paymentMethodData
                }, false);
            }

        } catch (error) {
            if (error?.statusCode === 'CANCELED') {
                if (onClose) {
                    onClose();
                }
                // User canceled, don't show error
                return;
            }
            if (onError) {
                onError(error?.statusMessage || error?.message || error);
            }
        }
    }, [
        paypal,
        onClick,
        onClose,
        onError,
        settings,
        needsShipping,
        paymentsClient
    ]);

    /**
     * Create and render Google Pay button
     */
    useEffect(() => {
        if (paymentsClient && settings?.config && buttonContainer && !button) {
            const {settings} = currentData.current;
            try {
                const googlePayButton = paymentsClient.createButton({
                    onClick: onButtonClick,
                    buttonBorderType: data.button.buttonBorderType,
                    buttonColor: data.button.buttonColor,
                    buttonType: data.button.buttonType,
                    buttonRadius: data.button.buttonRadius,
                    buttonLocale: data.button.buttonLocale,
                    buttonSizeMode: settings.isExpress ? 'fill' : data.button.buttonSizeMode,
                });
                googlePayButton.style.height = data.button.buttonHeight;
                buttonContainer.innerHTML = '';
                buttonContainer.appendChild(googlePayButton);
                setButton(googlePayButton);
            } catch (error) {
                console.error('Failed to create Google Pay button:', error);
                onError(error);
            }
        }

    }, [
        button,
        onError,
        onButtonClick,
        paymentsClient,
        buttonContainer
    ]);

    return {
        button
    };
};

/**
 * Build payment data request for Google Pay
 */
const buildPaymentDataRequest = (
    {
        settings,
        needsShipping
    }) => {
    const {
        merchantInfo,
        apiVersion,
        apiVersionMinor,
        allowedPaymentMethods
    } = settings.config;

    // Set phoneNumberRequired based on WooCommerce field requirements
    if (allowedPaymentMethods[0]?.parameters?.billingAddressParameters) {
        allowedPaymentMethods[0].parameters.billingAddressParameters.phoneNumberRequired = isFieldRequired('phone', 'billing', false);
    }

    const request = {
        apiVersion,
        apiVersionMinor,
        merchantInfo,
        allowedPaymentMethods,
        transactionInfo: {
            countryCode: settings.countryCode,
            currencyCode: settings.currencyCode,
            totalPriceStatus: 'FINAL',
            totalPrice: settings.total,
            displayItems: mapLineItems(settings.lineItems || []),
            totalPriceLabel: settings.i18n?.total || 'Total'
        },
        emailRequired: isFieldRequired('email', 'billing', true),
        callbackIntents: ['PAYMENT_AUTHORIZATION']
    };

    // Add shipping if needed
    if (needsShipping && settings.isExpress) {
        request.shippingAddressRequired = true;
        request.shippingOptionRequired = true;
        request.shippingAddressParameters = {};
        request.callbackIntents.push('SHIPPING_ADDRESS', 'SHIPPING_OPTION');

        if (settings.shippingOptions && settings.shippingOptions.length > 0) {
            request.shippingOptionParameters = {
                shippingOptions: mapShippingOptions(settings.shippingOptions, settings.currencyCode),
                ...(settings.selectedShippingMethod && {
                    defaultSelectedOptionId: settings.selectedShippingMethod
                })
            };
        }
    }

    return request;
};

export default useGooglePayButton;